import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatINR, formatApiErrorDetail } from "@/api/client";
import { MapPin, Truck, BadgeIndianRupee, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [address, setAddress] = useState({
    full_name: user?.name || "", phone: user?.phone || "", email: user?.email || "",
    line1: "", line2: "", city: "", district: "", state: "", pincode: "", landmark: "", is_default: false,
  });
  const [payment, setPayment] = useState("cod");
  const [placing, setPlacing] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);
  const shipping = 50;
  const handling = 24;
  const total = subtotal + shipping + handling;

  if (!user) {
    return (
      <div className="container-fah py-20 text-center">
        <h2 className="font-display text-2xl font-bold">Please login to checkout</h2>
        <Link to="/login" className="mt-4 inline-block bg-primary text-white font-semibold py-2.5 px-6 rounded-lg">Login</Link>
      </div>
    );
  }

  if (items.length === 0 && !placedOrder) {
    return <div className="container-fah py-20 text-center text-muted-foreground">Your cart is empty. <Link to="/products" className="text-primary hover:underline">Shop now</Link></div>;
  }

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((it) => ({ product_id: it.product_id, qty: it.qty })),
        address,
        payment_method: payment,
      });
      setPlacedOrder(data);
      clear();
      toast.success("Order placed successfully!");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail));
    } finally { setPlacing(false); }
  };

  if (placedOrder) {
    return (
      <div className="container-fah py-12 max-w-2xl mx-auto text-center" data-testid="order-confirmation">
        <div className="w-20 h-20 rounded-full bg-green-100 text-green-700 flex items-center justify-center mx-auto"><CheckCircle2 className="w-12 h-12" /></div>
        <h1 className="font-display text-3xl font-bold mt-4">Order Placed!</h1>
        <p className="text-muted-foreground mt-2">Thank you for shopping with FarmersAgroHub.</p>
        <div className="card-fah p-5 mt-6 text-left">
          <div className="text-sm">Order Number</div>
          <div className="font-display font-bold text-xl text-primary" data-testid="order-number">{placedOrder.order_number}</div>
          <div className="mt-4 space-y-1 text-sm">
            <Row label="Items" value={placedOrder.items.length} />
            <Row label="Payment" value={placedOrder.payment_method === "cod" ? "Cash on Delivery" : "Prepaid (Mock)"} />
            <Row label="Total" value={formatINR(placedOrder.total)} />
          </div>
        </div>
        <div className="mt-6 flex gap-3 justify-center">
          <Link to="/dashboard" className="bg-primary text-white font-semibold py-2.5 px-5 rounded-lg" data-testid="btn-view-orders">View My Orders</Link>
          <Link to="/products" className="border border-border bg-white font-semibold py-2.5 px-5 rounded-lg">Continue Shopping</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fah py-8 sm:py-12" data-testid="page-checkout">
      <h1 className="font-display text-3xl font-bold mb-6">Checkout</h1>
      <form onSubmit={placeOrder} className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="card-fah p-5">
            <div className="flex items-center gap-2 mb-4"><MapPin className="w-5 h-5 text-primary" /><h2 className="font-display font-bold">Delivery Address</h2></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input testid="addr-name" label="Full Name *" value={address.full_name} onChange={(e) => setAddress({ ...address, full_name: e.target.value })} required />
              <Input testid="addr-phone" label="Mobile Number *" value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} required />
              <Input testid="addr-email" label="Email (optional)" type="email" className="sm:col-span-2" value={address.email} onChange={(e) => setAddress({ ...address, email: e.target.value })} />
              <Input testid="addr-line1" label="House / Flat / Building No. *" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} required />
              <Input testid="addr-line2" label="Street / Society (optional)" value={address.line2} onChange={(e) => setAddress({ ...address, line2: e.target.value })} />
              <Input testid="addr-city" label="Village / City *" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} required />
              <Input testid="addr-district" label="District *" value={address.district} onChange={(e) => setAddress({ ...address, district: e.target.value })} required />
              <Input testid="addr-state" label="State *" value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} required />
              <Input testid="addr-pincode" label="PIN Code *" value={address.pincode} onChange={(e) => setAddress({ ...address, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} required maxLength={6} />
              <Input testid="addr-landmark" label="Landmark (optional)" className="sm:col-span-2" value={address.landmark} onChange={(e) => setAddress({ ...address, landmark: e.target.value })} />
            </div>
          </div>

          <div className="card-fah p-5">
            <div className="flex items-center gap-2 mb-4"><BadgeIndianRupee className="w-5 h-5 text-primary" /><h2 className="font-display font-bold">Payment Method</h2></div>
            <div className="space-y-2">
              <PayOption checked={payment === "cod"} onChange={() => setPayment("cod")} testid="pay-cod" title="Cash on Delivery" sub="Pay when you receive your order" badge="Recommended" />
              <PayOption checked={payment === "prepaid"} onChange={() => setPayment("prepaid")} testid="pay-prepaid" title="Prepaid (UPI/Card/Netbanking)" sub="Razorpay integration — coming soon. Demo mode for now." />
            </div>
          </div>
        </div>

        <div>
          <div className="card-fah p-5 sticky top-24">
            <h3 className="font-display font-bold text-lg mb-4">Order Summary</h3>
            <div className="space-y-1 text-sm max-h-48 overflow-y-auto mb-3">
              {items.map((it) => (
                <div key={it.product_id} className="flex justify-between gap-2">
                  <span className="truncate">{it.name} × {it.qty}</span>
                  <span className="font-medium shrink-0">{formatINR(it.price * it.qty)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-3 space-y-2 text-sm">
              <Row label="Subtotal" value={formatINR(subtotal)} />
              <Row label="Shipping" value={formatINR(shipping)} />
              <Row label="Handling Charges" value={formatINR(handling)} />
              <div className="flex justify-between font-bold text-base pt-2 border-t border-border"><span>Total</span><span>{formatINR(total)}</span></div>
            </div>
            <button disabled={placing} type="submit" className="mt-5 w-full bg-secondary hover:opacity-90 text-secondary-foreground font-bold py-3 rounded-lg disabled:opacity-50" data-testid="btn-place-order">
              {placing ? "Placing order…" : `Place Order — ${formatINR(total)}`}
            </button>
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"><Truck className="w-4 h-4" /> Estimated delivery in 3-7 business days</div>
          </div>
        </div>
      </form>
    </div>
  );
};

const Input = ({ label, className = "", testid, ...props }) => (
  <div className={className}>
    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
    <input data-testid={testid} {...props} className="mt-1 w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm" />
  </div>
);

const PayOption = ({ checked, onChange, testid, title, sub, badge }) => (
  <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${checked ? "border-primary bg-accent/40" : "border-border"}`} data-testid={testid}>
    <input type="radio" checked={checked} onChange={onChange} className="mt-1" />
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-sm">{title}</span>
        {badge && <span className="text-[10px] uppercase tracking-wide bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded">{badge}</span>}
      </div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
  </label>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span>{value}</span></div>
);

export default Checkout;
